import moment from 'moment/moment'
import { ReactNode } from 'react'

import { Icon } from '../components/Icon'
import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Payment } from './Payment'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { User } from './User'

type SourceConstructorArgs = ConstructorArgs<() => Source>

export enum SourcePaymentType {
    ANY = 'ANY',
    DEBIT = 'DEBIT',
    CREDIT = 'CREDIT',
    ANY_CARD = 'ANY_CARD',
    PAYOUT_CARD = 'PAYOUT_CARD',
    ACH_PAYMENT = 'ACH_PAYMENT',
}

export class Source extends BaseRecord {
    readonly recordType = 'source'

    name?: string
    type?: string
    // transient fields used for adding sources
    token?: string
    allowPayment?: boolean
    allowDeposit?: boolean
    // readonly fields used to determine usage type
    charge?: string
    transfer?: string
    // credit / debit card
    instant?: boolean
    cardBrand?: string
    cardNumber?: string
    cardScanned?: boolean
    securityCode?: string
    postalCode?: string
    countryCode?: string
    expiredDate?: moment.Moment
    // bank account
    bankName?: string
    routingNumber?: string
    accountNumber?: string

    verifiedDate?: moment.Moment
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    readonly displayName?: string

    constructor(data?: Partial<SourceConstructorArgs>) {
        super(data)
        if (!data) {
            this.countryCode = 'US'
            this.owner = PSApplication.me()
            return
        }

        this.name = data.name ?? undefined
        this.type = data.type ?? undefined
        this.charge = data.charge ?? undefined
        this.transfer = data.transfer ?? undefined
        this.allowPayment = !!data.allow_payment
        this.allowDeposit = !!data.transfer
        this.cardBrand = data.card_brand ?? undefined
        this.instant = data.instant ?? false
        this.cardNumber = data.card_number ?? undefined
        this.cardScanned = data.card_scanned ?? false
        this.postalCode = data.postal_code ?? undefined
        this.countryCode = data.country_code ?? undefined
        this.expiredDate = this.propertyFromDateArg(data.expired_date)
        this.bankName = data.bank_name ?? undefined
        this.routingNumber = data.routing_number ?? undefined
        this.accountNumber = data.account_number ?? undefined
        this.verifiedDate = this.propertyFromTimestampArg(data.verified_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.displayName = data.display_name ?? undefined
    }

    clone(overrides?: Partial<SourceConstructorArgs>): Source {
        return new Source({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Source {
        const source = CacheFactory.getInstance().getRecord(Source, id) ?? new Source({ id })
        source.countryCode = 'US'
        source.owner = PSApplication.me()
        return source
    }

    // todo: implement image getter
    get image(): number {
        if (this.type !== undefined && this.type === 'checking')
            return 1 //R.drawable.safe
        else if (this.type !== undefined && this.type === 'applepay')
            return 2 //R.drawable.applepay
        else if (this.type !== undefined && this.type === 'link') return 3 //R.drawable.stripe_link
        return 4 //R.drawable.credit_card
    }

    // todo: implement SQLiteFactory.executeSingleRowQuery sync
    // static instantSource(): Source | undefined {
    //     const result = SQLiteFactory.executeSingleRowQuery("select jsonData from sources where owner = ? and instant = '1' and cancelledDate is null order by createdDate desc", PSApplication.me().id)
    //     if (result !== undefined)
    //         return new Source(result.jsonForKey('jsonData'))
    //     return undefined
    // }

    static async defaultSource(): Promise<Source | undefined> {
        const me = PSApplication.me()
        return Source.defaultSourceByType(
            me.sp ? SourcePaymentType.CREDIT : SourcePaymentType.DEBIT
        )
    }

    static async defaultSourceByType(sourceType: SourcePaymentType): Promise<Source | undefined> {
        let defaultSource: Source | undefined = undefined
        const l = await Source.validSources()
        for (const source of l) {
            if (
                sourceType === SourcePaymentType.DEBIT &&
                source.verifiedDate !== undefined &&
                (source.type === 'debit' ||
                    source.type === 'credit' ||
                    source.type === 'prepaid' ||
                    source.type === 'applepay' ||
                    source.type === 'checking')
            )
                defaultSource = source
            if (
                sourceType === SourcePaymentType.CREDIT &&
                source.type === 'checking' &&
                !!source.transfer
            )
                defaultSource = source
            if (
                sourceType === SourcePaymentType.ANY &&
                (source.type === 'debit' || source.type === 'credit' || source.type === 'prepaid')
            )
                defaultSource = source
            if (defaultSource !== undefined) break
        }
        return defaultSource
    }

    static async defaultClientSource(
        sourceType: SourcePaymentType,
        clientId: string
    ): Promise<Source | undefined> {
        let defaultSource: Source | undefined = undefined
        const l = await Source.validClientSources(clientId)
        for (const source of l) {
            if (
                sourceType === SourcePaymentType.DEBIT &&
                source.verifiedDate !== undefined &&
                (source.type === 'debit' ||
                    source.type === 'credit' ||
                    source.type === 'prepaid' ||
                    source.type === 'applepay' ||
                    source.type === 'checking')
            )
                defaultSource = source
            if (sourceType === SourcePaymentType.CREDIT && source.type === 'checking')
                defaultSource = source
            if (
                sourceType === SourcePaymentType.ANY &&
                (source.type === 'debit' || source.type === 'credit' || source.type === 'prepaid')
            )
                defaultSource = source
            if (defaultSource !== undefined) break
        }
        return defaultSource
    }

    static async validSources(
        sourceType: SourcePaymentType = SourcePaymentType.ANY
    ): Promise<Source[]> {
        const me = PSApplication.me()
        const sources = await RecordFactory.getInstance().loadSources()

        return sources.filter((source) => {
            if (me.id !== source.owner?.id) return false
            if (source.cancelledDate) return false
            if (sourceType === SourcePaymentType.DEBIT && !source.verifiedDate) return false
            if (sourceType === SourcePaymentType.CREDIT && source.type !== 'checking') return false
            if (
                sourceType === SourcePaymentType.ACH_PAYMENT &&
                !(source.type === 'checking' && source.verifiedDate && source.charge)
            )
                return false
            if (
                sourceType === SourcePaymentType.ANY_CARD &&
                !['debit', 'credit', 'prepaid'].includes(source.type ?? '')
            )
                return false
            return true
        })
    }

    static async validClientSources(client: string): Promise<Source[]> {
        const me = PSApplication.me()
        const arr: Source[] = []
        const sources = await RecordFactory.getInstance().loadClientSources(client)
        for (const source of sources) {
            const owner = source.owner
            const creator = source.creator
            if (owner && client !== owner.id) continue
            if (
                creator &&
                !me.equals(creator) &&
                !me.equals(creator.employer) &&
                !(me.perm === User.PERM_ROLE_ADMIN && me.employer?.equals(creator.owner))
            )
                continue
            if (!creator && !me.equals(owner)) continue
            if (source.cancelledDate) continue
            arr.push(source)
        }
        return arr
    }

    static async validClientCardNumbersAndBrands(client: string): Promise<Source[]> {
        const arr: Source[] = []
        const sources = await Source.validClientSources(client)
        for (const source of sources) if (source.cardNumber && source.cardBrand) arr.push(source)
        return arr
    }

    get isBnpl(): boolean {
        return (
            this.type?.toLowerCase() === 'affirm' ||
            this.type?.toLowerCase() === 'klarna' ||
            this.type?.toLowerCase() === 'afterpay'
        )
    }

    static surchargeRate(source: Source | undefined): number | undefined {
        return source && source.type === 'checking'
            ? Payment.ACH_SURCHARGE_RATE
            : Payment.PAYMENT_SURCHARGE_RATE
    }

    get sourceDisplayString(): string {
        if (this.type === 'checking') {
            const last4 = this.accountNumber?.slice(-4)
            return `(••••${last4})`
        }
        else if (this.type === 'link') {
            return `(Link - ${this.name})`
        }
        else {
            const last4 = this.cardNumber?.slice(-4)
            return `(••••${last4})`
        }
    }

    get displayTitle() {
        return this.type === 'checking'
            ? `Checking - ${this.accountNumber?.slice(-4)}`
            : `${this.cardBrand} - ${this.cardNumber?.slice(-4)} ${this.instant ? '\u26A1' : ''}`
    }

    get displayBankName() {
        return this.bankName ?? (this.type === 'applepay' ? 'Apple Pay' : this.name)
    }

    get displayTitleWithEnding() {
        if (this.type === 'checking') {
            if (this.accountNumber === undefined) {
                return undefined
            }
            return `Checking ending in ${this.accountNumber.slice(-4)}`
        }
        else {
            if (this.cardNumber === undefined || this.cardBrand === undefined) {
                return undefined
            }
            return `${this.cardBrand} ending in ${this.cardNumber.slice(-4)}`
        }
    }

    get displayExpiresString() {
        return this.type === 'checking'
            ? !!this.verifiedDate
                ? 'Verified'
                : !!this.charge
                  ? 'Pending'
                  : ''
            : this.expiredDate
              ? `Expires ${this.expiredDate.format('MM/YY')}`
              : ''
    }

    get icon(): ReactNode {
        if (this.type === 'applepay') {
            return <Icon icon="applePay.payment" />
        }
        if (this.type === 'checking') {
            return <Icon icon="bank.medium" background="round" />
        }

        switch (this.cardBrand) {
            case 'Visa': {
                return <Icon icon="visa.payment" />
            }
            case 'MasterCard': {
                return <Icon icon="mastercard.payment" />
            }
            case 'Discover': {
                return <Icon icon="discover.payment" />
            }
            case 'American Express': {
                return <Icon icon="amex.payment" />
            }
            default:
                return <Icon className="w-9" icon="bankCard.large" />
        }
    }

    toString(): string {
        let nameString: string
        if (this.type === 'checking')
            nameString = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} - ${this.accountNumber!.slice(-4)}`
        else if (this.type === 'link')
            nameString = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} - ${this.name}`
        else
            nameString = `${this.cardBrand!.charAt(0).toUpperCase() + this.cardBrand!.slice(1)} - ${this.cardNumber ? this.cardNumber.slice(-4) : undefined}`
        return nameString
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.expiredDate) this.expiredDate = moment(data.expiredDate)
        if (data.verifiedDate) this.verifiedDate = moment(data.verifiedDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
    }

    static dataType(): DataType {
        return 'sources'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['owner'] = this.owner!.id
        if (this.creator) dict['creator'] = this.creator.id
        dict['type'] = this.type
        if (this.name) dict['name'] = this.name
        if (this.token) dict['token'] = this.token
        if (this.accountNumber) dict['account_number'] = this.accountNumber
        if (this.routingNumber) dict['routing_number'] = this.routingNumber
        dict['allow_payment'] = this.allowPayment
        dict['allow_deposit'] = this.allowDeposit
        dict['card_scanned'] = this.cardScanned
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}

// See the comment above BaseRecord.getSourceConstructor
;(window as any).PSSourceModel = Source
